import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNotifications } from '@/features/notifications';
import { useDeleteProjectPermitMutation, useUpdateProjectMutation, useUpdateProjectItemMutation } from '../api';
import { Project } from '../types';

export type UseUpdateProjectProps = {
    projectId: string;
    opportunityId: string;
    setDrawer: (drawer: null | string) => void;
};

export function useUpdateProject({ projectId, opportunityId, setDrawer }: UseUpdateProjectProps) {
    const { t } = useTranslation();
    const { add } = useNotifications();

    const [updateProject] = useUpdateProjectMutation();
    const [updateProjectItem] = useUpdateProjectItemMutation();
    const [deleteProjectPermit] = useDeleteProjectPermitMutation();

    const onSubmitProject = useCallback(
        (data: Partial<Project>, description?: string) => {
            updateProject({ id: projectId, data })
                .unwrap()
                .then(() => {
                    add({
                        id: new Date().toString(),
                        title: t('Project updated'),
                        description: description ?? t('Your project has been updated'),
                    });
                })
                .catch(() => {
                    add({
                        id: new Date().toString(),
                        title: t('ERROR'),
                        description: t('There was an error updating the project. Please try again later.'),
                    });
                })
                .then(() => {
                    setDrawer(null);
                });
        },
        [updateProject, projectId, opportunityId, setDrawer, add, t]
    );

    const onSubmitProjectItem = useCallback(
        (id: any, data: any, description?: string | null) => {
            updateProjectItem({ id, data, projectId: projectId, opportunityId: opportunityId })
                .unwrap()
                .then(() => {
                    add({
                        id: new Date().toString(),
                        title: t('Project updated'),
                        description: description ?? t('Your project has been updated'),
                    });
                })
                .catch(() => {
                    add({
                        id: new Date().toString(),
                        title: t('ERROR'),
                        description: t('There was an error updating the project. Please try again later.'),
                    });
                })
                .then(() => {
                    setDrawer(null);
                });
        },
        [t, projectId, opportunityId, add, updateProjectItem, setDrawer]
    );

    const deletePermitNew = useCallback(
        (id: any) => {
            deleteProjectPermit({ id, projectId: projectId })
                .unwrap()
                .then(() => {
                })
                .catch(() => {
                    add({
                        id: new Date().toString(),
                        title: t('ERROR'),
                        description: t('There was an error deleting the permit. Please try again later.'),
                    });
                });
        },
        [t, deleteProjectPermit, add]
    );

    return { onSubmitProject, onSubmitProjectItem, deletePermitNew };
}
